// ⚠ THIS MODULE IMPORTS NOTHING, DELIBERATELY. `layout.test.ts` runs under plain
// `node --experimental-strip-types`, which resolves ESM specifiers literally and so cannot follow
// the extensionless `./constants` the rest of the app is written with. Owning the scale
// constants keeps this a leaf the test can import, which is the whole reason the arithmetic was
// extracted here rather than left in `viewport.ts`.

/**
 * How far the reader may zoom BY HAND. This is the wheel's range and nothing else.
 *
 * ⚠⚠ IT IS NOT THE RANGE OF A FIT — see `FIT_MAX_SCALE`. The two were one number briefly and the
 * result was a graph blown up to fill whatever display it was opened on: a fresh account drew at
 * 4x on every window including a phone, and a 21-node account at 1.96x on an ordinary laptop.
 * Zooming in is the reader's decision to make, so the ceiling is generous HERE and absent from
 * the fit.
 */
export const SCALE_EXTENT: [number, number] = [0.25, 4]

/**
 * The largest scale a FIT will choose. One: a fit shrinks, it never enlarges.
 *
 * ⚠⚠ THE FIT ANSWERS "DOES IT ALL SHOW", NOT "DOES IT FILL THE SCREEN". Those read as the same
 * job on a crowded fleet and diverge completely on a small one, where there is nothing to fit and
 * enlarging is pure magnification: the nodes, the labels and the gaps between them all grow, so
 * the reader gets a 21-node account rendered at twice its natural size and no more information
 * than 456x317 carried. Benoit, 2026-09-11: *"the graph is way too big, make it normal size (the
 * user can always zoom)."*
 *
 * ⚠ What this does NOT give back is the old `.scale(1)`: a graph too large for the window is
 * still scaled down to fit it, which is the half of the fit that was always right. Only the
 * enlarging half is gone.
 */
export const FIT_MAX_SCALE = 1

/** The largest gutter `fitMargin` will leave on one side. */
export const FIT_MARGIN = 90

/**
 * The gutter on one axis, for a window of `extent` px on that axis.
 *
 * ⚠⚠ A SHARE OF THE AXIS, CAPPED — NOT A CONSTANT. `FIT_MARGIN` on both sides of a 375 px phone
 * is 180 px of gutter around 195 px of graph, so a flat constant spends half a small screen on
 * whitespace and shrinks the picture the reader has least room for. Above roughly 750 px the cap
 * binds and this is exactly `FIT_MARGIN`, which is what a desktop had before.
 */
export function fitMargin(extent: number): number {
  return Math.min(FIT_MARGIN, extent * 0.12)
}

/**
 * The scale that fits content of `contentWidth` x `contentHeight` inside a `viewWidth` x
 * `viewHeight` window, leaving room for the labels — which hang off their glyphs and are NOT in
 * the extent, because the extent is measured from node positions.
 *
 * ⚠⚠ THIS LIVES IN ITS OWN MODULE SO THERE IS ONE OF IT. `viewport.ts` needs a DOM and `d3`, and
 * a test that cannot import it would otherwise keep its own copy of this arithmetic — which is
 * precisely how `layout.test.ts` came to hold private copies of `buildDegreeMap` and
 * `buildSiblingMap` and let the real force model drift while the gate stayed green (app#309's
 * review, finding 3). The pure half is extracted rather than duplicated.
 *
 * ⚠ Clamped BELOW by `SCALE_EXTENT`, so a fit can never park the graph at a zoom the reader
 * cannot get back from by hand, and ABOVE by `FIT_MAX_SCALE`, which is 1 — natural size.
 *
 * ⚠ A single node, or none, has no extent to fit; `1` is the honest answer rather than a division
 * by zero that would reach d3 as `NaN` and blank the canvas.
 */
export function fitScale(
  contentWidth: number,
  contentHeight: number,
  viewWidth: number,
  viewHeight: number
): number {
  const usableWidth = viewWidth - fitMargin(viewWidth) * 2
  const usableHeight = viewHeight - fitMargin(viewHeight) * 2
  if (contentWidth <= 0 || contentHeight <= 0 || usableWidth <= 0 || usableHeight <= 0) return 1

  const scale = Math.min(usableWidth / contentWidth, usableHeight / contentHeight)
  const [min] = SCALE_EXTENT
  return Math.max(min, Math.min(FIT_MAX_SCALE, scale))
}
